/**
 * client/input/layouts.ts — DualSense physical buttons -> engine commands (pure, unit-tested).
 *
 * OWNER: INPUT agent.
 * Sources: research/controller.md §1 (button names / W3C standard indices), §2.1 button layouts
 * (tools/src/bo2-raw/buttons_*.cfg via research/data/controller.json; menu names from
 * ui/t6/buttonlayoutoptions.lua), Flipped triggers (MED/ESTIMATE: swaps L1<->L2, R1<->R2).
 */
import type { ButtonLayout } from '../settings/settings';

/** Physical DualSense buttons, numbered like the W3C "standard" Gamepad mapping (+ touchpad 17). */
export const Pad = {
  CROSS: 0, CIRCLE: 1, SQUARE: 2, TRIANGLE: 3, L1: 4, R1: 5, L2: 6, R2: 7, CREATE: 8, OPTIONS: 9,
  L3: 10, R3: 11, DPAD_UP: 12, DPAD_DOWN: 13, DPAD_LEFT: 14, DPAD_RIGHT: 15, PS: 16, TOUCHPAD: 17,
} as const;
export type Pad = (typeof Pad)[keyof typeof Pad];
export const PAD_BUTTON_COUNT = 18;
export const PAD_NAMES: readonly string[] = [
  'CROSS', 'CIRCLE', 'SQUARE', 'TRIANGLE', 'L1', 'R1', 'L2', 'R2', 'CREATE', 'OPTIONS', 'L3', 'R3',
  'DPAD_UP', 'DPAD_DOWN', 'DPAD_LEFT', 'DPAD_RIGHT', 'PS', 'TOUCHPAD',
];

/** Engine console commands the layouts bind (the `+cmd` names from the cfgs). */
export type EngineCmd =
  | 'attack' | 'speed_throw' | 'toggleads_throw' | 'frag' | 'smoke' | 'melee' | 'breath_sprint'
  | 'gostand' | 'stance' | 'usereload' | 'weapnext_inventory'
  | 'actionslot1' | 'actionslot2' | 'actionslot3' | 'actionslot4' | 'togglescores' | 'togglemenu';

type LayoutTable = Partial<Record<Pad, EngineCmd>>;

/** buttons_*.cfg [CTL §2.1]. Tactical = buttons_experimental. */
const BASE: Readonly<Record<ButtonLayout, LayoutTable>> = {
  default: {
    [Pad.R2]: 'attack', [Pad.L2]: 'speed_throw', [Pad.R1]: 'frag', [Pad.L1]: 'smoke', [Pad.R3]: 'melee',
    [Pad.L3]: 'breath_sprint', [Pad.CROSS]: 'gostand', [Pad.CIRCLE]: 'stance', [Pad.SQUARE]: 'usereload',
    [Pad.TRIANGLE]: 'weapnext_inventory',
  },
  tactical: {
    [Pad.R2]: 'attack', [Pad.L2]: 'speed_throw', [Pad.R1]: 'frag', [Pad.L1]: 'smoke', [Pad.R3]: 'stance',
    [Pad.L3]: 'breath_sprint', [Pad.CROSS]: 'gostand', [Pad.CIRCLE]: 'melee', [Pad.SQUARE]: 'usereload',
    [Pad.TRIANGLE]: 'weapnext_inventory',
  },
  lefty: {
    [Pad.R2]: 'speed_throw', [Pad.L2]: 'attack', [Pad.R1]: 'smoke', [Pad.L1]: 'frag', [Pad.R3]: 'breath_sprint',
    [Pad.L3]: 'melee', [Pad.CROSS]: 'gostand', [Pad.CIRCLE]: 'stance', [Pad.SQUARE]: 'usereload',
    [Pad.TRIANGLE]: 'weapnext_inventory',
  },
  nomad: {
    [Pad.R2]: 'attack', [Pad.L2]: 'toggleads_throw', [Pad.R1]: 'frag', [Pad.L1]: 'smoke', [Pad.R3]: 'stance',
    [Pad.L3]: 'breath_sprint', [Pad.CROSS]: 'gostand', [Pad.CIRCLE]: 'melee', [Pad.SQUARE]: 'usereload',
    [Pad.TRIANGLE]: 'weapnext_inventory',
  },
  charlie: {
    [Pad.R2]: 'attack', [Pad.L2]: 'frag', [Pad.R1]: 'toggleads_throw', [Pad.L1]: 'smoke', [Pad.R3]: 'melee',
    [Pad.L3]: 'breath_sprint', [Pad.CROSS]: 'gostand', [Pad.CIRCLE]: 'stance', [Pad.SQUARE]: 'usereload',
    [Pad.TRIANGLE]: 'weapnext_inventory',
  },
};

/**
 * Bindings common to every layout: D-pad actionslots 1-4, Options = pause menu, Create (PS3
 * Select/BACK) = togglescores. buttons_nomad.cfg has no BACK bind (relies on defaults, ESTIMATE),
 * so it is bound everywhere. Touchpad click = scoreboard too (PS4/PS5 CoD convention, ESTIMATE).
 */
const COMMON: LayoutTable = {
  [Pad.DPAD_UP]: 'actionslot1', [Pad.DPAD_DOWN]: 'actionslot2', [Pad.DPAD_LEFT]: 'actionslot3', [Pad.DPAD_RIGHT]: 'actionslot4',
  [Pad.OPTIONS]: 'togglemenu', [Pad.CREATE]: 'togglescores', [Pad.TOUCHPAD]: 'togglescores',
};

/** Full physical->command table for a layout, with the Flipped trigger option applied. */
export function resolveButtonLayout(layout: ButtonLayout, flipped: boolean): readonly (EngineCmd | null)[] {
  const base = BASE[layout] ?? BASE.default;
  const out: (EngineCmd | null)[] = new Array(PAD_BUTTON_COUNT).fill(null);
  for (const [k, v] of Object.entries(COMMON)) out[Number(k)] = v;
  for (const [k, v] of Object.entries(base)) {
    let phys = Number(k) as Pad;
    if (flipped) {
      // TRIGGERS_FLIPPED: bumpers <-> triggers on top of the layout [CTL §2.1, MED/ESTIMATE]
      if (phys === Pad.L1) phys = Pad.L2; else if (phys === Pad.L2) phys = Pad.L1;
      else if (phys === Pad.R1) phys = Pad.R2; else if (phys === Pad.R2) phys = Pad.R1;
    }
    out[phys] = v;
  }
  return out;
}

/** Inverse lookup for UI prompts ("press ○ to crouch"): first physical button bound to cmd. */
export function padForCommand(table: readonly (EngineCmd | null)[], cmd: EngineCmd): Pad | -1 {
  const i = table.indexOf(cmd);
  return i as Pad | -1;
}
